import React, { useState } from 'react';
import { useGetOne, Loading, useGetIdentity, useUpdate, useNotify, Form } from 'react-admin';
import { Typography, Box, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { RichTextInput, RichTextInputToolbar, FormatButtons } from 'ra-input-rich-text';

const Comment = ({ note, isLatest = false }) => {
    const { data, isLoading } = useGetOne('notes', { id: note });
    const { identity, isLoading: identityLoading } = useGetIdentity();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [update, { isPending }] = useUpdate();
    const notify = useNotify();

    if (isLoading || identityLoading || !data) {
        return <Loading />;
    }

    const { text, user, createdAt, updatedAt } = data;
    const date = new Date(createdAt);
    const isEdited = new Date(updatedAt) - new Date(createdAt) > 60000;
    const isAuthor = identity?.username === user;
    const canEdit = isLatest && isAuthor;

    const handleSave = (values) => {
        update(
            'notes',
            { id: data.id, data: { text: values.text }, previousData: data },
            {
                onSuccess: () => {
                    setDialogOpen(false);
                    notify('Note updated.');
                },
                onError: () => {
                    notify('Failed to update note.', { type: 'error' });
                },
            }
        );
    };

    return (
        <>
            <Box sx={{ marginBottom: 6 }}>
                <Box sx={{ borderRadius: 5, padding: '10px 30px', marginBottom: 2, backgroundColor: '#F0F0F0', maxWidth: 660, position: 'relative' }}>
                    <div dangerouslySetInnerHTML={{ __html: text }} />
                    {canEdit && (
                        <IconButton
                            size="small"
                            onClick={() => setDialogOpen(true)}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                            aria-label="Edit note"
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                    <div style={{ width: 0, height: 0, borderLeft: '20px solid transparent', borderRight: '20px solid transparent', borderTop: '20px solid #F0F0F0', margin: '0 0 -20px' }} />
                </Box>
                <Box sx={{ marginLeft: 4 }}>
                    <Typography variant="body2">{user}</Typography>
                    <Typography variant="caption">{date.toLocaleString()}</Typography>
                    {isEdited && (
                        <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 1, fontStyle: 'italic', cursor: 'default' }}
                            title={new Date(updatedAt).toLocaleString()}
                        >
                            edited
                        </Typography>
                    )}
                </Box>
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <Form defaultValues={{ text }} onSubmit={handleSave}>
                    <DialogTitle>Edit Note</DialogTitle>
                    <DialogContent>
                        <RichTextInput
                            source="text"
                            label={false}
                            fullWidth
                            toolbar={
                                <RichTextInputToolbar>
                                    <FormatButtons size="small" />
                                </RichTextInputToolbar>
                            }
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={isPending}>Save</Button>
                    </DialogActions>
                </Form>
            </Dialog>
        </>
    );
};

export default Comment;
